import { useState, useEffect, useCallback } from 'react';

interface StorageOptions {
  storage?: Storage;
  serialize?: (value: any) => string;
  deserialize?: (value: string) => any;
}

export function usePersistentStorage<T>(
  key: string,
  initialValue: T,
  options: StorageOptions = {}
) {
  const {
    storage = localStorage,
    serialize = JSON.stringify,
    deserialize = JSON.parse,
  } = options;

  // 获取初始值
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = storage.getItem(key);
      return item ? deserialize(item) : initialValue;
    } catch (error) {
      console.warn(`Error reading ${key} from storage:`, error);
      return initialValue;
    }
  });

  // 设置值
  const setValue = useCallback((value: T | ((val: T) => T)) => {
    try {
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      storage.setItem(key, serialize(valueToStore));
    } catch (error) {
      console.warn(`Error setting ${key} in storage:`, error);
    }
  }, [key, storedValue, storage, serialize]);

  // 移除值
  const removeValue = useCallback(() => {
    try {
      storage.removeItem(key);
      setStoredValue(initialValue);
    } catch (error) {
      console.warn(`Error removing ${key} from storage:`, error);
    }
  }, [key, initialValue, storage]);

  // 监听storage变化（其他标签页的更改）
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === key && e.newValue !== null) {
        try {
          setStoredValue(deserialize(e.newValue));
        } catch (error) {
          console.warn(`Error deserializing ${key} from storage event:`, error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [key, deserialize]);

  return [storedValue, setValue, removeValue] as const;
}